import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { ChartConfiguration, ChartOptions } from 'chart.js';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StatisticsService } from 'src/app/services/core/statistics/statistics.service';

@UntilDestroy()
@Component({
  selector: 'app-statistics-graph-view',
  templateUrl: './statistics-graph-view.component.html',
  styleUrls: ['./statistics-graph-view.component.css']
})
export class StatisticsGraphViewComponent implements OnInit, OnDestroy {
  todayDate: any;
  companies: any = [];
  kpiData: any;

  companyBarChartData!: ChartConfiguration<'bar'>['data'];
  companyBarChartOptions!: ChartOptions<'bar'>;

  companyPieChartData!: ChartConfiguration<'pie'>['data'];
  companyPieChartOptions!: ChartOptions<'pie'>;

  doughnutChartData!: ChartConfiguration<'doughnut'>['data'];
  doughnutChartOptions!: ChartOptions<'doughnut'>;

  radarChartData!: ChartConfiguration<'radar'>['data'];
  radarChartOptions!: ChartOptions<'radar'>;

  // 🎨 Centralized color palette
  private readonly chartColors = {
    red: { bg: 'rgba(248, 113, 113, 0.6)', border: 'rgba(255, 99, 132, 1)' },
    green: { bg: 'rgba(74, 222, 128, 0.6)', border: 'rgba(75, 192, 192, 1)' },
    blue: { bg: 'rgba(54, 162, 235, 0.7)', border: 'rgba(54, 162, 235, 1)' },
    yellow: { bg: 'rgba(255, 206, 86, 0.7)', border: 'rgba(255, 206, 86, 1)' },
    purple: { bg: 'rgba(153, 102, 255, 0.7)', border: 'rgba(153, 102, 255, 1)' }
  };

  constructor(private statisticsService: StatisticsService) {}

  ngOnInit(): void {
    this.todayDate = [moment().startOf('day').toISOString(), moment().endOf('day').toISOString()];
    this.getKpi();
  }

  getKpi() {
    const queryparams = { time: [this.todayDate[0], this.todayDate[1]] };

    this.statisticsService.get(queryparams).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data && data.length) {
        const [{ companies, ...kpiData }] = data;
        this.kpiData = kpiData;
        this.companies = companies.sort((a: any, b: any) => (b.total || 0) - (a.total || 0));

        this.buildCompanyBarChart();
        this.buildCompanyPieChart();
        this.buildCompanyDoughnutChart();
        this.buildCompanyRadarChart();
      }
    });
  }

  // 📊 Bar Chart
  buildCompanyBarChart() {
    this.companyBarChartData = {
      labels: this.companies.map((c: any) => {
        const name = c?.company?.name ?? 'unassigned';
        return name.length > 12 ? name.substring(0, 12) + '…' : name;
      }),
      datasets: [
        {
          label: 'Cancelled',
          data: this.companies.map((c: any) => c?.totalCancelled ?? 0),
          backgroundColor: this.chartColors.red.bg,
          borderColor: this.chartColors.red.border
        },
        {
          label: 'Completed',
          data: this.companies.map((c: any) => c?.totalCompleted ?? 0),
          backgroundColor: this.chartColors.green.bg,
          borderColor: this.chartColors.green.border
        }
      ]
    };

    this.companyBarChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true }
      },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items: any) => {
              const idx = items[0].dataIndex;
              return this.companies[idx]?.company?.name ?? 'unassigned';
            },
            label: (ctx: any) => {
              const ds = ctx.dataset;
              const val = ctx.raw as number;
              const total = this.companies[ctx.dataIndex]?.total ?? 0;
              const pct = total ? ` (${((val / total) * 100).toFixed(1)}%)` : '';
              return `${ds.label}: ${val}${pct}`;
            },
            footer: (items: any) => {
              const idx = items[0].dataIndex;
              return `Total: ${this.companies[idx]?.total ?? 0}`;
            }
          }
        }
      }
    };
  }

  // 🥧 Pie Chart
  buildCompanyPieChart() {
    const totalCancelled = this.companies.reduce((sum: number, c: any) => sum + (c?.totalCancelled ?? 0), 0);
    const totalCompleted = this.companies.reduce((sum: number, c: any) => sum + (c?.totalCompleted ?? 0), 0);
    const totalInprogress = this.companies.reduce((sum: number, c: any) => sum + (c?.totalInprogress ?? 0), 0);

    this.companyPieChartData = {
      labels: ['Cancelled', 'Completed', 'Inprogress'],
      datasets: [
        {
          data: [totalCancelled, totalCompleted, totalInprogress],
          backgroundColor: [this.chartColors.red.bg, this.chartColors.green.bg,this.chartColors.blue.bg],
          borderColor: [this.chartColors.red.border, this.chartColors.green.border,this.chartColors.blue.border],
          borderWidth: 1
        }
      ]
    };

    this.companyPieChartOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.raw as number;
              const total = totalCancelled + totalCompleted;
              const pct = total ? ` (${((val / total) * 100).toFixed(1)}%)` : '';
              return `${ctx.label}: ${val}${pct}`;
            }
          }
        }
      }
    };
  }

  // 🍩 Doughnut Chart
  buildCompanyDoughnutChart() {
    const topCompanies = this.companies.slice(0, 5);
    const palette = [this.chartColors.red, this.chartColors.blue, this.chartColors.yellow, this.chartColors.green, this.chartColors.purple];

    this.doughnutChartData = {
      labels: topCompanies.map((c: any) => c?.company?.name ?? 'unassigned'),
      datasets: [
        {
          data: topCompanies.map((c: any) => c?.total ?? 0),
          backgroundColor: palette.map(c => c.bg),
          borderColor: palette.map(c => c.border),
          borderWidth: 1
        }
      ]
    };

    this.doughnutChartOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.raw as number;
              const total = topCompanies.reduce((sum: number, c: any) => sum + (c?.total ?? 0), 0);
              const pct = total ? ` (${((val / total) * 100).toFixed(1)}%)` : '';
              return `${ctx.label}: ${val}${pct}`;
            }
          }
        }
      }
    };
  }

  // 📡 Radar Chart
  buildCompanyRadarChart() {
    const topCompanies = this.companies.slice(0, 5);

    this.radarChartData = {
      labels: topCompanies.map((c: any) => c?.company?.name ?? 'unassigned'),
      datasets: [
        {
          label: 'Cancelled',
          data: topCompanies.map((c: any) => c?.totalCancelled ?? 0),
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          borderColor: this.chartColors.red.border,
          pointBackgroundColor: this.chartColors.red.border
        },
        {
          label: 'Completed',
          data: topCompanies.map((c: any) => c?.totalCompleted ?? 0),
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: this.chartColors.green.border,
          pointBackgroundColor: this.chartColors.green.border
        },
          {
          label: 'Inprogress',
          data: topCompanies.map((c: any) => c?.totalInprogress ?? 0),
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: this.chartColors.blue.border,
          pointBackgroundColor: this.chartColors.blue.border
        }
      ]
    };

    this.radarChartOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.raw as number;
              return `${ctx.dataset.label}: ${val}`;
            }
          }
        }
      },
      scales: {
        r: { beginAtZero: true, ticks: { stepSize: 1 } }
      }
    };
  }

  onDateChange(event: any) {
    this.todayDate = [
      event[0].toISOString(),
      moment(event[1]).endOf('day').toISOString()
    ];
    this.kpiData = null;
    this.companies = null;
    this.getKpi();
  }

  ngOnDestroy(): void {}
}
